import { Dispatch, SetStateAction, useState, useEffect, useCallback } from "react";
declare global {
  interface WindowEventMap {
    "local-storage": CustomEvent;
  }
}
type SetValue<T> = Dispatch<SetStateAction<T>>;
/**
 * Хук для управления состоянием, синхронизированным с localStorage.
 * Всегда пытается прочитать значение из localStorage при инициализации.
 * @param initialValue Значение по умолчанию, используемое, если в localStorage нет данных.
 * @param key Ключ для localStorage.
 * @returns [Текущее состояние, Функция для установки состояния, Функция для удаления из localStorage]
 */
export const useLocalStorage = <T,>(initialValue: T, key: string): [T, SetValue<T>, () => void] => {
  // Функция для получения значения: сначала из localStorage, затем initialValue

  const getValue = useCallback((): T => {
    // Проверка на SSR
    if (typeof window === "undefined") {
      return initialValue;
    }
    try {
      const storage = localStorage.getItem(key);
      if (storage) {
        const data = JSON.parse(storage);
        // Простая проверка на null/undefined. Можно добавить более сложные проверки типов.
        if (data !== null && data !== undefined) {
          // Опционально можно добавить проверку типа: typeof data === typeof initialValue
          return data as T;
        }
      }
      // Возвращаем initialValue, если в localStorage нет данных или данные невалидны
      return initialValue;
    } catch (e) {
      // console.error(`Error reading localStorage key "${key}":`, e); // Логируем ошибки чтения
      return initialValue; // Возвращаем initialValue в случае ошибки
    }
  }, [initialValue, key]);
  // Состояние инициализируется ОДИН РАЗ значением из хранилища или initialValue
  const [state, setState] = useState<T>(getValue);
  // Эффект для сохранения состояния в localStorage при каждом изменении состояния
  useEffect(() => {
    // Проверка на SSR
    if (typeof window === "undefined") {
      return;
    }
    // Избегаем сохранения null или undefined
    if (state === null || state === undefined) {
      // Опционально можно удалить элемент, если состояние стало null/undefined
      // localStorage.removeItem(key);
      return;
    }
    try {
      const stringifiedState = JSON.stringify(state);
      // Можно добавить проверку на равенство текущему значению в localStorage
      // для предотвращения излишних записей, но эффект уже срабатывает
      // только при изменении состояния, что обычно достаточно.
      localStorage.setItem(key, stringifiedState);
      // console.log(`Saved state for key "${key}": ${stringifiedState}`); // Отладочный вывод
    } catch (error) {
      // console.error(`Error writing to localStorage key "${key}":`, error); // Логируем ошибки записи
    }
  }, [state, key]); // Эффект зависит от состояния и ключа

  const removeStorage = useCallback(() => {
    // Проверка на SSR
    if (typeof window === "undefined") return;
    try {
      localStorage.removeItem(key);
      // Опционально можно сбросить состояние компонента после удаления из хранилища
      // setState(initialValue);
    } catch (error) {
      // console.error(`Error removing localStorage key "${key}":`, error); // Логируем ошибки удаления
    }
  }, [key]);

  return [state, setState, removeStorage];
};
